import React, { useContext } from 'react';
import { FaSyncAlt } from 'react-icons/fa';

import { Tooltip } from '@getethos/ethos-design-system-v2';

import { faExclamation } from '@fortawesome/pro-solid-svg-icons/faExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { isAdmin, isManager } from '../../../../common/actionHelper';
import {
  FlexCol,
  FlexRow,
  Tag,
  TitleSmall,
} from '../../../../common/components/v2';
import { Resource } from '../../../../common/constants/resource';
import { NoraPermissionV2 } from '../../../../common/constants/roles';
import { TaskState } from '../../../../common/constants/states';
import { getFormattedTimestamp } from '../../../../common/helpers';
import { useAccess } from '../../../../common/hooks/useAccess';
import ConfirmationDeleteButton from '../../../layout/ConfirmationDeleteButton';
import { AuthContext } from '../../../login/AuthWrapper';
import { ITask } from '../../../tasks/interfaces/ITask';
import { TaskTypes } from '../../../tasks/types/taskTypes';
import styles from '../../styles.module.scss';
import { NoraTaskIcon } from './NoraTaskIcon';

export const TaskDetailsTitledItem = ({
  task,
  title,
  children,
  taskStateHandler,
  retryHandler,
  markOutdatedHandler,
  dataLabel,
  deleteAttachmentHandler,
}: {
  task: ITask;
  title: string;
  children: React.ReactNode;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  retryHandler?: (event: any) => void;
  markOutdatedHandler?: (event: any) => void;
  dataLabel?: string;
  deleteAttachmentHandler?: (
    taskId: number,
    taskType: TaskTypes,
    attachmentId: number,
  ) => void;
}) => {
  const canOutdateTask = useAccess({
    permission: NoraPermissionV2.OUTDATE_TASK,
    legacyCheck: roles => isAdmin(roles) || isManager(roles),
  });
  const auth = useContext(AuthContext);
  const canDeleteAttachment = isAdmin(auth.roles);
  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };
  const outdatedMarkableTaskStates = [
    TaskState.WAIVED,
    TaskState.ERROR,
    TaskState.COMPLETED,
  ];
  return (
    <>
      <FlexRow
        data-label={dataLabel ?? ''}
        id={task.id.toString()}
        className={styles.Title}
      >
        <FlexCol className={styles.TitleTaskIcon}>
          <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
        </FlexCol>
        <div className={styles.TitleContainer}>
          <TitleSmall>{title}</TitleSmall>
          {/*
            At any point in time, there should be either no pill or at most one pill.
            When the evidence is "outdated," the "repulled pill" should be hidden.
            The "outdated" pill takes precedence over the "repulled" pill.
          */}
          {task.outdated ? (
            <FlexCol className={styles.RulePills}>
              <Tag type="red">{Resource.EVIDENCE_OUTDATED}</Tag>
            </FlexCol>
          ) : task.retriedAt ? (
            <FlexCol className={styles.RulePills}>
              <Tag type="green">{Resource.EVIDENCE_REPULLED}</Tag>
            </FlexCol>
          ) : null}
        </div>
        <FlexCol className={styles.CreatedDate}>
          {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
        </FlexCol>
        {canOutdateTask &&
          markOutdatedHandler &&
          !task.outdated &&
          outdatedMarkableTaskStates.includes(task.state) && (
            <Tooltip content={Resource.MARK_OUTDATED} placement="bottom">
              <div>
                <button
                  className={styles.RepullButton}
                  onClick={markOutdatedHandler}
                >
                  <FontAwesomeIcon
                    icon={faExclamation}
                    style={{ width: '13px' }}
                  />
                </button>
              </div>
            </Tooltip>
          )}
        {retryHandler && (
          <Tooltip content={Resource.REPULL_EVIDENCE} placement="bottom">
            <div>
              <button className={styles.RepullButton} onClick={retryHandler}>
                <FaSyncAlt />
              </button>
            </div>
          </Tooltip>
        )}
        {task.attachment?.documentId && deleteAttachmentHandler && (
          <ConfirmationDeleteButton
            canDelete={canDeleteAttachment}
            dialogTitle={Resource.DELETE_ATTACHMENT}
            dialogContentText={Resource.DELETE_ATTACHMENT_CONFIRMATION_TEXT}
            deleteHandler={() =>
              deleteAttachmentHandler(
                task.id,
                task.taskType,
                task.attachment!.documentId,
              )
            }
          />
        )}
      </FlexRow>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      {children}
    </>
  );
};
